import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import type { HostedAgentSession, HostedAgentSummary } from "@/api/client/hosted-agents"

import { AlertCircle, Bot } from "@/components/icons"
import { ModulePageHeader } from "@/components/layouts/module-page-header"
import { Button } from "@/components/ui/button"
import { EmptyState } from "@/components/ui/empty-state"
import { useRoutes } from "@/contexts/use-routes"
import { useAgentRunsQuery } from "@/features/ai-explorer/hooks/use-agent-runs-query"
import { useSessionsQuery } from "@/features/chat/hooks/use-sessions-query"
import { useCurrentSessionType } from "@/hooks/use-current-session-type"

import { AgentRoster } from "./agent-roster"
import { useHostedAgents } from "../hooks/use-hosted-agents"
import { agentRosterPresence } from "../utils/agent-presence-utils"

/**
 * Agents roster (HERMES Fas 4): one card per hosted agent the user may see.
 * Selecting an agent navigates to the detail view (`/agents/:agentSlug`),
 * where the thread list and (next phase) the chat view live.
 */
export function AgentsModule() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { getRoutePath } = useRoutes()
  const { data: agents, isPending, isError, refetch } = useHostedAgents()
  const sessionType = useCurrentSessionType()
  const sessionsQuery = useSessionsQuery(sessionType)
  const runsQuery = useAgentRunsQuery()
  const presenceById = agentRosterPresence(
    (sessionsQuery.data ?? []) as HostedAgentSession[],
    runsQuery.data ?? []
  )

  const basePath = getRoutePath("agents")

  const openAgent = (agent: HostedAgentSummary) => {
    void navigate(`${basePath}/${agent.slug}`)
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-y-auto [--radius:0.75rem]">
      <ModulePageHeader
        className="mx-auto w-full max-w-7xl px-4 pt-8 sm:px-8"
        subtitle={t("agents.roster.subtitle")}
        title={t("agents.roster.title")}
      />
      <div className="mx-auto w-full max-w-7xl flex-1 px-4 pb-8 sm:px-8">
        {isPending && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }, (_, index) => (
              <div key={index} className="skeleton-warm h-40 rounded-lg" />
            ))}
          </div>
        )}

        {isError && (
          <EmptyState
            description={t("agents.roster.error_description")}
            icon={AlertCircle}
            title={t("agents.roster.error_title")}
            primaryAction={
              <Button onClick={() => void refetch()}>{t("agents.roster.retry")}</Button>
            }
          />
        )}

        {/* Deliberate DESIGN.md deviation: no primary CTA. Agent assignment is
            an admin action (AccessPolicy), so there is no workflow an end user
            could start from here — the description tells them who to ask. */}
        {!isPending && !isError && agents.length === 0 && (
          <EmptyState
            description={t("agents.roster.empty_description")}
            icon={Bot}
            title={t("agents.roster.empty_title")}
          />
        )}

        {!isPending && !isError && agents.length > 0 && (
          <AgentRoster agents={agents} presenceById={presenceById} onOpen={openAgent} />
        )}
      </div>
    </div>
  )
}
